import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import Colors from '../../utils/color';

/**
 * Shared geometry for the app's screen-level floating notices: the app-update
 * banner (RefreshBanner), the task-router advisory (TaskRouterOnBanner) and the
 * issue-flag undo toast (HITL-1499). All three occupy the same slot — centred at
 * `top: 100px`, below the 64px header and above the panes — so an app-level
 * notice always appears in one known place.
 *
 * Extracted rather than copy-pasted a third time: the block was already
 * duplicated between the first two, and a third copy would make the drift
 * permanent.
 *
 * Every prop not named below is spread onto the ROOT div, and that is not
 * incidental. The agent-interception guards listen on `document` in the CAPTURE
 * phase and match with `target.closest(selector)`, so a `data-*` marker on an
 * inner element could never opt a notice out — each caller puts its marker and
 * its testid on the root through `...rest`.
 */
interface FloatingBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Left accent rule, and the icon tint. Omit for neither (RefreshBanner). */
  accent?: string;
  icon?: React.ReactNode;
  /** The message. */
  children: React.ReactNode;
  /** Action buttons — use BannerActionButton. */
  actions?: React.ReactNode;
  maxWidth?: number | string;
}

/** Which of the family's two action treatments a BannerActionButton wears. */
export type BannerActionVariant = 'primary' | 'danger';

interface BannerActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BannerActionVariant;
}

// The four hover/active shades below are not in `Colors`; they stay literal, but
// now in one file instead of two.
const useStyles = makeStyles<Theme>(({ spacing }) => ({
  banner: {
    position: 'absolute',
    top: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'fit-content',
    background: Colors.daintree,
    color: Colors.white,
    paddingTop: spacing(1.5),
    paddingBottom: spacing(1.5),
    paddingLeft: '20px',
    paddingRight: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing(1.5),
    zIndex: 9999,
  },
  icon: {
    display: 'inline-flex',
    flexShrink: 0,
  },
  action: {
    border: 'none',
    padding: `${spacing(1)} ${spacing(1.5)}`,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background-color 0.2s, transform 0.2s, box-shadow 0.2s',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
  primary: {
    background: Colors.amber,
    color: Colors.primaryButtonTextColor,
    '&:hover:not(:disabled)': {
      background: '#FFD54F',
      transform: 'scale(1.05)',
      boxShadow: '0 4px 8px rgba(255, 193, 7, 0.4)',
    },
    '&:active:not(:disabled)': {
      background: '#FFB300',
      transform: 'scale(0.98)',
    },
  },
  danger: {
    background: Colors.torchRed,
    color: Colors.white,
    '&:hover:not(:disabled)': {
      background: '#FF3366',
      transform: 'scale(1.05)',
      boxShadow: '0 4px 8px rgba(255, 0, 72, 0.4)',
    },
    '&:active:not(:disabled)': {
      background: '#CC0036',
      transform: 'scale(0.98)',
    },
  },
}));

/**
 * The family's action button: uppercase, bold, amber (`primary`) or red
 * (`danger`). `disabled` is fully supported — RefreshBanner needs it for its 5s
 * re-arm; the other two callers never disable.
 *
 * `data-variant` is rendered so a test can assert which treatment a button got
 * without reaching for a generated class name.
 */
export const BannerActionButton = ({
  variant = 'primary',
  children,
  ...rest
}: BannerActionButtonProps) => {
  const classes = useStyles();
  return (
    <button
      className={`${classes.action} ${classes[variant]}`}
      data-variant={variant}
      {...rest}
    >
      {children}
    </button>
  );
};

const FloatingBanner = ({
  accent,
  icon,
  children,
  actions,
  maxWidth,
  style,
  ...rest
}: FloatingBannerProps) => {
  const classes = useStyles();

  return (
    <div
      className={classes.banner}
      // Accent and width are per-caller, so they ride inline rather than
      // spawning a makeStyles class per notice. A caller's own `style` wins.
      style={{
        ...(accent ? { borderLeft: `4px solid ${accent}` } : {}),
        ...(maxWidth === undefined ? {} : { maxWidth }),
        ...style,
      }}
      {...rest}
    >
      {icon && (
        // MUI SvgIcon fills with `currentColor`, so tinting the wrapper tints
        // the icon without every caller styling its own svg.
        <span
          className={classes.icon}
          style={accent ? { color: accent } : undefined}
        >
          {icon}
        </span>
      )}
      {children}
      {actions}
    </div>
  );
};

export default FloatingBanner;
